import React, { useState, useCallback } from 'react';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { MapPin, Plus, X, Users, Type, Edit3, Copy } from 'lucide-react';
import { useAreaTagRecords, usePhotoMarkers } from '../hooks/hooks';
import { useCreateViewerAreaTag } from '../hooks/useAreaTagCommands';
import { ImageDimensions, Position, PhotoMarker, Layout } from '../types/types';
import {
  AreaTagDrawingOverlay,
  findItemsInLayoutPolygon,
  layoutPolygonsOverlap,
  normalizeAreaTagPolygon,
} from '@ironview/interactive-viewer';
import { ExportAreaTagsDialog } from './ExportAreaTagsDialog';

interface Ironview360AreaTagControlsProps {
  selectedLayoutId: string;
  projectId: string;
  layouts?: Layout[];
  isPdfLayout?: boolean;
  imageDimensions: ImageDimensions;
  position: Position;
  scale: number;
  onWheelZoom?: (e: React.WheelEvent) => void;
  onTouchStart?: (e: React.TouchEvent) => void;
  onTouchMove?: (e: React.TouchEvent) => void;
  onTouchEnd?: (e: React.TouchEvent) => void;
  onTextEditingModeChange?: (textEditingMode: boolean, editingAreaId?: number | null) => void;
  onSaveTextChanges?: () => void;
  onCancelTextChanges?: () => void;
  onPolygonEditingModeChange?: (polygonEditingMode: boolean) => void;
  pendingPolygonChanges?: Map<number, number[][]>;
}

/**
 * Ultra-minimal area tags overlay - just essential functionality
 */
export const Ironview360AreaTagControls: React.FC<Ironview360AreaTagControlsProps> = ({
  selectedLayoutId,
  projectId,
  layouts = [],
  isPdfLayout = false,
  imageDimensions,
  position,
  scale,
  onWheelZoom,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onTextEditingModeChange,
  onSaveTextChanges,
  onCancelTextChanges,
  onPolygonEditingModeChange,
  pendingPolygonChanges = new Map(),
}) => {
  // State management
  const [editMode, setEditMode] = useState<'view' | 'create' | 'textEdit' | 'polygonEdit'>('view');
  const [showAreaForm, setShowAreaForm] = useState(false);
  const [areaName, setAreaName] = useState('');
  const [detectedMarkers, setDetectedMarkers] = useState<PhotoMarker[]>([]);
  const [manualMarkerIds, setManualMarkerIds] = useState('');
  const [currentPolygon, setCurrentPolygon] = useState<number[][]>([]);
  const [showExportDialog, setShowExportDialog] = useState(false);

  // Data hooks
  const { data: areaTags = [], isLoading } = useAreaTagRecords(selectedLayoutId, projectId);
  const { data: photoMarkers = [] } = usePhotoMarkers(selectedLayoutId, projectId);
  const createAreaTag = useCreateViewerAreaTag(selectedLayoutId, projectId);

  // Handle area creation - now shows form with detected markers
  const handleAreaCreated = useCallback(
    async (points: number[][]) => {
      const newPolygon = normalizeAreaTagPolygon(points as [number, number][]);
      const hasOverlap = areaTags.some((area) => {
        const existing = normalizeAreaTagPolygon(
          area.points as Array<[number, number] | { x: number; y: number }>
        );
        return layoutPolygonsOverlap(newPolygon, existing);
      });
      if (hasOverlap) {
        alert('This area overlaps an existing area. Please draw a non-overlapping polygon.');
        return;
      }

      // Detect markers within the polygon
      const markersInPolygon = findItemsInLayoutPolygon(
        photoMarkers,
        points as [number, number][],
        (marker) => marker
      );
      setDetectedMarkers(markersInPolygon);
      setCurrentPolygon(points);
      setShowAreaForm(true);
      setEditMode('view'); // Exit create mode
    },
    [areaTags, photoMarkers]
  );

  // Handle form submission
  const handleFormSubmit = useCallback(async () => {
    if (!areaName.trim()) return;

    // Parse manual marker IDs and find those markers
    const manualIds = manualMarkerIds
      .split(',')
      .map((id) => parseInt(id.trim()))
      .filter((id) => !isNaN(id));

    const manualMarkers = photoMarkers.filter((marker) => manualIds.includes(marker.number));

    // Combine detected and manual markers
    const allMarkers = [...detectedMarkers, ...manualMarkers];

    // Get marker row IDs as the canonical area association.
    const markerIds = allMarkers
      .map((marker) => marker.dbId)
      .filter((id): id is number => typeof id === 'number');

    // Keep photo IDs as a transitional supplement for older payload handlers.
    const allPhotoIds = allMarkers.flatMap((marker) => marker.photos.map((photo) => photo.id));

    // Remove duplicates
    const uniquePhotoIds = [...new Set(allPhotoIds)];

    try {
      await createAreaTag.mutateAsync({
        name: areaName.trim(),
        points: currentPolygon.map(([x, y]) => [x, y] as const),
        markerIds: [...new Set(markerIds)],
        photoIds: uniquePhotoIds,
      });

      // Reset form
      setShowAreaForm(false);
      setAreaName('');
      setManualMarkerIds('');
      setDetectedMarkers([]);
      setCurrentPolygon([]);
    } catch (error) {
      console.error('❌ Failed to create area:', error);
      alert('Failed to create area. Please try again.');
    }
  }, [
    createAreaTag,
    selectedLayoutId,
    areaName,
    currentPolygon,
    detectedMarkers,
    manualMarkerIds,
    photoMarkers,
  ]);

  return (
    <div className="absolute inset-0 z-10 pointer-events-none">
      {/* Drawing Overlay - only render when actively drawing */}
      {editMode === 'create' && (
        <div className="w-full h-full flex items-center justify-center pointer-events-auto">
          <AreaTagDrawingOverlay
            active
            isPdfLayout={isPdfLayout}
            dimensions={imageDimensions}
            position={position}
            scale={scale}
            guides={areaTags}
            onComplete={(points) => handleAreaCreated(points.map(([x, y]) => [x, y]))}
            onWheel={onWheelZoom}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          />
        </div>
      )}

      {/* Minimal Floating Controls - Always accessible */}
      <div className="absolute top-4 left-4 z-20 pointer-events-auto">
        <div className="flex flex-col gap-2">
          <Button
            variant={editMode === 'create' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setEditMode(editMode === 'create' ? 'view' : 'create')}
            className="shadow-lg"
          >
            {editMode === 'create' ? (
              <>
                <X className="h-4 w-4 mr-2" />
                Cancel Drawing
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Create Area
              </>
            )}
          </Button>

          {/* Text Editing Mode Button - Only show if areas exist and not in create mode */}
          {areaTags.length > 0 && editMode !== 'create' && (
            <Button
              variant={editMode === 'textEdit' ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                if (editMode === 'textEdit') {
                  setEditMode('view');
                  onTextEditingModeChange?.(false, null);
                } else {
                  setEditMode('textEdit');
                  onTextEditingModeChange?.(true, null);
                }
              }}
              className="shadow-lg"
            >
              <Type className="h-4 w-4 mr-2" />
              {editMode === 'textEdit' ? 'Exit Label Edit' : 'Edit Labels'}
            </Button>
          )}

          {/* Polygon Editing Button - Only show when areas exist and not in create mode */}
          {areaTags.length > 0 && editMode !== 'create' && (
            <Button
              variant={editMode === 'polygonEdit' ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                if (editMode === 'polygonEdit') {
                  // Check if there are pending changes when exiting polygon edit mode
                  if (pendingPolygonChanges && pendingPolygonChanges.size > 0) {
                    const shouldSave = window.confirm(
                      `You have unsaved polygon changes to ${pendingPolygonChanges.size} area(s). Do you want to save them before exiting?\n\nClick OK to save and exit, or Cancel to discard changes and exit.`
                    );

                    if (shouldSave) {
                      onSaveTextChanges?.(); // Save changes (this handles both text and polygon)
                    } else {
                      onCancelTextChanges?.(); // Discard changes
                    }
                  }
                  // Exit polygon editing mode
                  setEditMode('view');
                  onPolygonEditingModeChange?.(false);
                } else {
                  // Enter polygon editing mode
                  setEditMode('polygonEdit');
                  onPolygonEditingModeChange?.(true);
                }
              }}
              className="shadow-lg"
            >
              <Edit3 className="h-4 w-4 mr-2" />
              {editMode === 'polygonEdit'
                ? pendingPolygonChanges && pendingPolygonChanges.size > 0
                  ? `Save & Exit (${pendingPolygonChanges.size} changes)`
                  : 'Exit Polygon Edit'
                : 'Edit Polygons'}
            </Button>
          )}

          {/* Export Areas Button - Only show when areas exist and not in create mode */}
          {areaTags.length > 0 && editMode !== 'create' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowExportDialog(true)}
              className="shadow-lg"
            >
              <Copy className="h-4 w-4 mr-2" />
              Export Areas
            </Button>
          )}

          {/* Text Editing Instructions - Only show when in text edit mode */}
          {editMode === 'textEdit' && (
            <Card className="shadow-lg">
              <CardContent className="p-2">
                <div className="text-xs space-y-1">
                  <div className="font-medium text-gray-700 mb-1">Text Editing:</div>
                  <div>• Click or tap any area polygon</div>
                  <div>• Label dialog opens with current name</div>
                  <div>• Save updates immediately and syncs to server</div>
                  <div>• Cancel closes without changing label</div>
                  <div>• Use "Exit Label Edit" when done</div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Polygon Editing Instructions - Only show when in polygon edit mode */}
          {editMode === 'polygonEdit' && (
            <Card className="shadow-lg">
              <CardContent className="p-2">
                <div className="text-xs space-y-1">
                  <div className="font-medium text-gray-700 mb-1">Polygon Editing:</div>
                  <div>• Click any polygon to select it</div>
                  <div>
                    • <span className="text-blue-600">Blue numbered circles</span>: Drag to move
                    vertices
                  </div>
                  <div>
                    • <span className="text-yellow-500">Yellow circles</span>: Snap to nearby
                    vertices/edges
                  </div>
                  <div>
                    •{' '}
                    <span className="font-medium text-orange-600">
                      Changes are saved when you exit
                    </span>
                  </div>
                  <div>• Press ESC to finish editing</div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Hotkeys Guide - Only show when drawing */}
          {editMode === 'create' && (
            <Card className="shadow-lg">
              <CardContent className="p-2">
                <div className="text-xs space-y-1">
                  <div className="font-medium text-gray-700 mb-1">Drawing Assistance:</div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Undo point:</span>
                    <code className="text-blue-600 font-mono">Cmd+Z</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Horizontal:</span>
                    <code className="text-blue-600 font-mono">Shift+H</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Vertical:</span>
                    <code className="text-blue-600 font-mono">Shift+V</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Parallel:</span>
                    <code className="text-orange-600 font-mono">Shift+P</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Same Length:</span>
                    <code className="text-purple-600 font-mono">Shift+L</code>
                  </div>
                  <div className="text-xs text-gray-500 mt-2 pt-1 border-t">
                    <div>
                      • <span className="text-orange-600">Orange</span>: Parallel lines
                    </div>
                    <div>
                      • <span className="text-purple-600">Purple</span>: Same length
                    </div>
                    <div>• Auto-snaps when close</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Area Count Badge - Only show if areas exist */}
      {areaTags.length > 0 && (
        <div className="absolute top-4 right-4 z-20 pointer-events-auto">
          <Badge variant="secondary" className="shadow-lg px-3 py-1">
            <MapPin className="h-3 w-3 mr-1" />
            {areaTags.length} area{areaTags.length !== 1 ? 's' : ''}
          </Badge>
        </div>
      )}

      {/* Area Creation Form - Shows after drawing polygon */}
      {showAreaForm && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-40 pointer-events-auto">
          <Card className="w-96 max-w-[90%] shadow-xl">
            <CardContent className="p-6 bg-white">
              <h3 className="text-lg font-semibold mb-4">Create New Area</h3>

              {/* Area Name Input */}
              <div className="mb-4">
                <Label htmlFor="area-name">Area Name</Label>
                <Input
                  id="area-name"
                  type="text"
                  value={areaName}
                  onChange={(e) => setAreaName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && areaName.trim()) {
                      handleFormSubmit();
                    }
                  }}
                  placeholder="Enter area name..."
                  className="mt-1"
                  autoFocus
                />
              </div>

              {/* Detected Markers */}
              {detectedMarkers.length > 0 && (
                <div className="mb-4">
                  <Label className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Detected Markers ({detectedMarkers.length})
                  </Label>
                  <div className="mt-2 p-3 bg-gray-50 rounded-md">
                    <div className="flex flex-wrap gap-1">
                      {detectedMarkers.map((marker) => (
                        <Badge key={marker.id} variant="outline" className="text-xs">
                          #{marker.number}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Manual Marker IDs Input */}
              <div className="mb-6">
                <Label htmlFor="manual-markers">Additional Marker IDs (optional)</Label>
                <Input
                  id="manual-markers"
                  type="text"
                  value={manualMarkerIds}
                  onChange={(e) => setManualMarkerIds(e.target.value)}
                  placeholder="e.g., 25, 36, 42"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Comma-separated marker numbers to include manually
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAreaForm(false);
                    setAreaName('');
                    setManualMarkerIds('');
                    setDetectedMarkers([]);
                    setCurrentPolygon([]);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button onClick={handleFormSubmit} disabled={!areaName.trim()} className="flex-1">
                  Create Area
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Export Areas Dialog */}
      <div className="pointer-events-auto">
        <ExportAreaTagsDialog
          open={showExportDialog}
          onOpenChange={setShowExportDialog}
          layouts={layouts}
          currentLayoutId={selectedLayoutId}
          projectId={projectId}
          sourceAreaTags={areaTags}
        />
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-30">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-sm">Loading areas...</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
